import React, { Dispatch, FC, SetStateAction, useMemo } from 'react'

import {
  Button,
  Divider,
  LegacyModal,
  LegacyModalProvider,
} from '@joor/design-system'
import { Form, Formik } from 'formik'
import ceil from 'lodash/ceil'
import omit from 'lodash/omit'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'

import {
  ManualPaymentMethod,
  ManualTransactionFragment,
} from '__generated__/atlas-types'

import { Spacer } from 'styles/components'
import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import usePortal from 'hooks/usePortal'
import { getUserEmail } from 'store/currentUser/selectors'

import { ManualPaymentMethodMapping } from '.'
import {
  ManualPaymentsFormContainer,
  StyledModal,
} from './ManualTransactions.styles'
import useManualTransactionCRUD from './useManualTransactionCRUD'
import {
  DropdownOptionType,
  Field,
  InputType,
} from 'features/Orders/PublicInvoice/components/Forms'
import { Order } from 'features/Orders/types'

export type CreateEditMTOrderInfo = Pick<
  Order,
  'id' | 'currencyCode' | 'retailer' | 'brand'
>

interface PropsType {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  orderInfo: CreateEditMTOrderInfo
  manualTransaction?: ManualTransactionFragment
  remainingAmount: number
}

const ValidationSchema = Yup.object().shape({
  paymentMethod: Yup.object().required('Required'),
  paymentDate: Yup.string().required('Required'),
  amount: Yup.number()
    .when('maxAmount', (maxAmount: number, schema: Yup.NumberSchema) =>
      schema.max(maxAmount, 'Value is greater than remaining balance'),
    )
    .required('Required'),
  referenceNumber: Yup.string()
    .notRequired()
    .matches(/^[a-zA-Z0-9]+$/, 'Cannot contain special characters.'),
})

interface FormValuesType {
  paymentMethod?: DropdownOptionType<ManualPaymentMethod, string>
  paymentDate: Date
  amount?: string
  currency: string
  referenceNumber?: string | null
  recordedBy: string | null
  notes?: string | null
  maxAmount: number
}

const CreateEditManualTransactionModal: FC<PropsType> = ({
  isOpen,
  setIsOpen,
  orderInfo,
  manualTransaction,
  remainingAmount,
}) => {
  const userEmail: string = useSelector(getUserEmail)
  const isEditMode = !!manualTransaction
  const Portal = usePortal()
  const { trackEvent } = useUserAnalytics()

  const {
    createManualTransaction,
    updateManualTransaction,
    deleteManualTransaction,
  } = useManualTransactionCRUD(setIsOpen, manualTransaction?.id)

  const emptyInitialValues = {
    paymentDate: new Date(),
    currency: orderInfo.currencyCode,
    recordedBy: userEmail,
  }
  const initialValues = useMemo(
    () => ({
      ...emptyInitialValues,
      maxAmount: ceil(remainingAmount + (manualTransaction?.amount ?? 0), 2),
      ...(manualTransaction && {
        ...omit(manualTransaction, ['id', 'orderId', '__typename']),
        paymentMethod: {
          value: manualTransaction.paymentMethod,
          label: ManualPaymentMethodMapping[manualTransaction.paymentMethod],
        } as DropdownOptionType<ManualPaymentMethod, string>,
        amount: manualTransaction.amount.toString(),
      }),
    }),
    [manualTransaction, remainingAmount],
  )

  const PaymentMethodDropdownOptions = Object.entries(
    ManualPaymentMethodMapping,
  ).map(([key, value]) => ({
    value: key,
    label: value,
  })) as DropdownOptionType<ManualPaymentMethod, string>[]

  const submitHandler = async ({
    paymentMethod,
    amount,
    maxAmount: _maxAmount,
    ...values
  }: FormValuesType) => {
    const input = {
      paymentMethod: paymentMethod!.value,
      amount: parseFloat(amount!),
      ...values,
    }

    if (isEditMode) {
      await updateManualTransaction({
        variables: {
          input: {
            id: manualTransaction!.id,
            ...input,
          },
        },
      })
    } else {
      await createManualTransaction({
        variables: {
          input: {
            brandId:
              toGlobalId('Account', fromGlobalId(orderInfo.brand.id).id) ?? '',
            retailerId:
              toGlobalId('Account', fromGlobalId(orderInfo.retailer?.id).id) ??
              '',
            orderId: orderInfo.id,
            ...input,
          },
        },
      })
    }
    trackEvent('JP_Click_Save_ManualPayment')
  }

  const deleteTransactionHandler = async (id: string) => {
    await deleteManualTransaction({
      variables: {
        input: {
          id,
        },
      },
    })
  }

  return (
    <Portal>
      <LegacyModalProvider>
        <StyledModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          disableBackgroundClick
        >
          <LegacyModal.Title>{`${
            isEditMode ? 'Edit' : 'Create'
          } Manual Transaction`}</LegacyModal.Title>
          <Formik<FormValuesType>
            initialValues={initialValues}
            onSubmit={submitHandler}
            validationSchema={ValidationSchema}
            validateOnMount
          >
            {({ isSubmitting, isValid }) => (
              <Form>
                <LegacyModal.Content>
                  <ManualPaymentsFormContainer>
                    <div>
                      <div>
                        <Field
                          inputType={InputType.Dropdown}
                          name="paymentMethod"
                          label="Payment Method"
                          options={PaymentMethodDropdownOptions}
                          placeholder="Select"
                          required
                          isClearable={false}
                        />
                        <Field
                          inputType={InputType.Datepicker}
                          name="paymentDate"
                          label="Date"
                          placeholder="Select Date"
                          required
                        />
                      </div>
                      <div>
                        <Field
                          inputType={InputType.NumberInput}
                          name="amount"
                          label="Amount"
                          placeholder="0.0"
                          required
                          isCurrency
                        />
                        <Field
                          inputType={InputType.Input}
                          name="currency"
                          label="Invoice Currency"
                          readOnly
                        />
                      </div>
                      <div>
                        <Field
                          inputType={InputType.Input}
                          name="referenceNumber"
                          label="Reference Number"
                          caption="For example, a check or account number."
                          maxLength={20}
                        />
                      </div>
                    </div>
                    <div>
                      <Field
                        inputType={InputType.Input}
                        name="recordedBy"
                        label="Recorded by"
                        readOnly
                      />
                      <Field
                        inputType={InputType.TextArea}
                        name="notes"
                        label="Notes/Comments"
                        maxLength={250}
                        onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
                          if (e?.target?.value) {
                            trackEvent('JP_Click_Notes_ManualPayment')
                          }
                        }}
                      />
                    </div>
                  </ManualPaymentsFormContainer>
                </LegacyModal.Content>
                <Divider />
                <Spacer topMargin="-30px" />
                <LegacyModal.Actions
                  {...(isEditMode && {
                    extraAction: {
                      label: 'Delete Record',
                      handler: () => {
                        deleteTransactionHandler(manualTransaction!.id)
                        trackEvent('JP_Click_Delete_ManualPayment')
                      },
                    },
                  })}
                >
                  <Button type="submit" disabled={isSubmitting || !isValid}>
                    Save Manual Record
                  </Button>
                  <LegacyModal.CloseButton>Cancel</LegacyModal.CloseButton>
                </LegacyModal.Actions>
              </Form>
            )}
          </Formik>
        </StyledModal>
      </LegacyModalProvider>
    </Portal>
  )
}
export default CreateEditManualTransactionModal
